import React, { useState, useRef } from "react";
import { Card, Button, Dropdown, Modal } from "react-bootstrap";
import { BsThreeDots } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import styles from "./sections.module.scss";
import {
  addOrUpdateVitalReport,
  getPatientIdSlice,
  deleteReportSlice,
} from "../../../../store/slices/patientSlice";
import {
  generateLinkToken,
  initiateLinking,
} from "../../../../store/slices/shareAbhaDataSlice";
import { BsCheckCircle } from "react-icons/bs";

const EcgSvg = () => (
  <svg
    className={styles.ecgSvg}
    viewBox="0 0 100 20"
    preserveAspectRatio="none"
  >
    <path
      className={styles.ecgPath}
      d="M0,10 L10,10 L12,5 L14,15 L16,10 L26,10 L28,8 L30,12 L32,10 L42,10 L44,9 L46,11 L48,10 L58,10 L60,7 L62,13 L64,10 L74,10 L76,10 L86,10 L88,9 L90,11 L92,10 L100,10"
    />
  </svg>
);

const VitalReportSection = ({ patientId, patientData }) => {
  const dispatch = useDispatch();
  const { clinicInfo } = useSelector((state) => state.clinic);
  const { token } = useSelector((state) => state.auth);
  const clinicId =clinicInfo?.[0]?.clinicId ||null;
  const [showModal, setShowModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [showInvalidTokenModal, setShowInvalidTokenModal] = useState(false);
  const [isFinalSharing, setIsFinalSharing] = useState(false);
  const [showLinkedSuccessModal, setShowLinkedSuccessModal] = useState(false);
  const [showLinkedErrorModal, setShowLinkedErrorModal] = useState(false);
  const [viewReport, setViewReport] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedReports, setSelectedReports] = useState([]);

  const [formData, setFormData] = useState({
    consultationDate: "",
    heightFeet: "",
    heightInch: "",
    weight: "",
    bmi: "",
    pulse: "",
    spo2: "",
    temperature: "",
    respiratoryRate: "",
    bloodPressure: { systolic: "", diastolic: "" },
    status: "Unshared",
    patient_report: [],
  });

  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [fileError, setFileError] = useState("");

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === "systolic" || name === "diastolic") {
      setFormData((prev) => ({
        ...prev,
        bloodPressure: {
          ...prev.bloodPressure,
          [name]: value,
        },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const vitalReports =
    patientData?.reportDetails
      ?.filter(
        (r) =>
          r.reportType === "Vital Report" &&
          String(r.clinicId || "") === String(clinicId || ""),
      )
      ?.sort(
        (a, b) => new Date(b.consultationDate) - new Date(a.consultationDate),
      ) || [];

  const latestReport = vitalReports[0] || null;

  const handleViewAll = (report = latestReport) => {
    setViewReport(report);
    setShowModal(true);
  };
  const handleClose = () => {
    setShowModal(false);
    setViewReport(null);
  };

  const handleAdd = () => {
    setEditingReport(null);
    setFormData({
      consultationDate: "",
      heightFeet: "",
      heightInch: "",
      weight: "",
      bmi: "",
      pulse: "",
      spo2: "",
      temperature: "",
      respiratoryRate: "",
      bloodPressure: { systolic: "", diastolic: "" },
      status: "Unshared",
    });
    setFiles([]);
    setFileError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
    setShowAddModal(true);
  };

  const handleEdit = (report) => {
    setEditingReport(report);

    let feet = "";
    let inch = "";
    if (report.height && report.height.includes("/")) {
      const parts = report.height.split("/");
      feet = parts[0].trim(); 
      inch = parts[1].trim(); 
    }

    setFormData({
      consultationDate: report.consultationDate
        ? (() => {
            const d = new Date(report.consultationDate);
            const pad = (n) => String(n).padStart(2, "0");

            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
              d.getDate(),
            )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
          })()
        : "",

      heightFeet: feet,
      heightInch: inch,
      weight: report.weight || "",
      bmi: report.bmi || "",
      pulse: report.pulse || "",
      spo2: report.spo2 || "",
      temperature: report.temperature || "",
      respiratoryRate: report.respiratoryRate || "",
      bloodPressure: {
        systolic: report.bloodPressure?.systolic || "",
        diastolic: report.bloodPressure?.diastolic || "",
      },
      status: report.status || "Unshared",
      patient_report: report.patient_report || [],
    });

    setShowAddModal(true);
  };

  const handleAddClose = () => setShowAddModal(false);

  const handleFileChange = (e) => {
    setFileError("");

    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    const validFiles = selectedFiles.filter((file) => file.size <= 1024 * 1024);
    const oversizedFiles = selectedFiles.filter(
      (file) => file.size > 1024 * 1024,
    );

    if (oversizedFiles.length > 0) {
      setFileError("Each file must be less than or equal to 1MB.");
    }

    if (validFiles.length === 0) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    const newIsAllImages = validFiles.every((f) => f.type.startsWith("image/"));
    const newIsAllPDFs = validFiles.every((f) => f.type === "application/pdf");

    const currentIsAllImages = files.every((f) => f.type.startsWith("image/"));
    const currentIsAllPDFs = files.every((f) => f.type === "application/pdf");

    const newType = newIsAllImages ? "image" : newIsAllPDFs ? "pdf" : null;
    const currentType = currentIsAllImages
      ? "image"
      : currentIsAllPDFs
        ? "pdf"
        : null;
    if (!newType) {
      setFileError("Please select only images OR only PDFs, not both.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    if (files.length > 0 && newType !== currentType) {
      setFileError(
        "You can only select one type of file. Mixing formats is not allowed.",
      );
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setFiles((prev) => [...prev, ...validFiles]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const convertJpgToPng = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = function (event) {
        const img = new Image();
        img.onload = function () {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);

          canvas.toBlob(
            (blob) => {
              if (!blob) return reject("PNG conversion failed");
              const pngFile = new File(
                [blob],
                file.name.replace(/\.(jpg|jpeg)$/i, ".png"),
                {
                  type: "image/png",
                },
              );
              resolve(pngFile);
            },
            "image/png",
            1.0,
          );
        };
        img.src = event.target.result;
      };

      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const confirmDelete = (reportId) => {
    setReportToDelete(reportId);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!reportToDelete) return;

    try {
      await dispatch(
        deleteReportSlice({ patientId, reportId: reportToDelete }),
      ).unwrap();
      await dispatch(getPatientIdSlice({ token, patientId })).unwrap();
    } catch (err) {
      console.error("Error deleting report:", err);
    } finally {
      setShowDeleteModal(false);
      setReportToDelete(null);
    }
  };

  const handleFormSubmit = async () => {

    if (!patientId || !clinicId) {
      console.error("Missing required data:", { patientId, clinicId });
      alert("Patient ID or Clinic ID missing. Please try again.");
      return;
    }

    const consultationDateISO = formData.consultationDate
      ? new Date(formData.consultationDate).toISOString()
      : new Date().toISOString();

    const reportData = {
      ...(editingReport?._id && { _id: editingReport._id }),

      consultationDate: consultationDateISO,
      height:
        formData?.heightFeet && formData?.heightInch
          ? `${formData?.heightFeet} / ${formData?.heightInch}`
          : "",
      weight: formData?.weight ?? "",
      bmi: formData?.bmi ?? "",
      pulse: formData?.pulse ?? "",
      spo2: formData?.spo2 ?? "",
      temperature: formData?.temperature ?? "",
      respiratoryRate: formData?.respiratoryRate ?? "",
      bloodPressure: {
        systolic: formData?.bloodPressure?.systolic ?? "",
        diastolic: formData?.bloodPressure?.diastolic ?? "",
      },
      status: formData?.status,
      patient_report: formData?.patient_report || [],
    };

    const formDataToSend = new FormData();
    formDataToSend.append("patientId", patientId);
    formDataToSend.append("clinicId", clinicId);
    formDataToSend.append("report", JSON.stringify(reportData));

    const convertedFiles = await Promise.all(
      files.map(async (file) => {
        if (/\.(jpe?g)$/i.test(file.name)) {
          return await convertJpgToPng(file);
        }
        return file;
      }),
    );

    convertedFiles.forEach((file) => formDataToSend.append("file", file));

    try {
      await dispatch(
        addOrUpdateVitalReport({ formDataToSend, token }),
      ).unwrap();
      await dispatch(getPatientIdSlice({ token, patientId })).unwrap();
      setShowAddModal(false);
      setEditingReport(null); 
      setFiles([]);
      setFileError("");
    } catch (err) {
      console.error("Error saving vital report:", err);
    }
  };

 

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const toSelect = vitalReports
        .filter((item) => !["Shared", "Transferred"].includes(item.status))
        .map((item) => item._id);
      setSelectedReports(toSelect);
    } else {
      setSelectedReports([]);
    }
  };

  const handleCheckboxChange = (id, isDisabled) => {
    if (isDisabled) return;
    setSelectedReports((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const handleShareClick = async () => {
    try {
      setIsSharing(true);

      const res = await dispatch(
        generateLinkToken({
          clinicId,
          userId: patientData?.userId?._id,
          token,
        }),
      );
      setIsSharing(false);

      if (res?.payload?.success) {
        setShowShareModal(true);
      } else {
        setShowInvalidTokenModal(true);
      }
    } catch (err) {
      console.error("Error generating link token:", err);
      setIsSharing(false);
      setShowInvalidTokenModal(true);
    }
  };

  const handleCloseInvalidModal = () => {
    setShowInvalidTokenModal(false);
  };


  const handleShareVitalReport = async () => {
    const reportsToShare = vitalReports.filter((r) =>
      selectedReports.includes(r._id),
    );

    if (reportsToShare.length === 0) return;

    const careContextData = reportsToShare.map((report) => ({
      referenceNumber: `VR-${report._id}`,
      display: `Vital Report - ${new Date(report.consultationDate).toLocaleDateString("en-GB")}`,
    }));

    const body = {
      patientId,
      hiTypes: "WellnessRecord",
      clinicId,
      careContextData,
    };

    try {
      setIsFinalSharing(true);
      const res = await dispatch(initiateLinking({ ...body, token })).unwrap();
      if (res?.success) {
        setShowLinkedSuccessModal(true);
        setSelectedReports([]);
      } else {
        setShowLinkedErrorModal(true);
      }
    } catch (err) {
      setShowLinkedErrorModal(true);
    } finally {
      setIsFinalSharing(false);
      setShowShareModal(false);
      dispatch(getPatientIdSlice({ token, patientId }));
    }
  };

  return (
    <div className={styles.vitalReportWrapperCenter}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex gap-2">
          <Button variant="primary" size="sm" onClick={handleAdd}>
            + Add
          </Button>
          <Button
            variant="outline-info"
            size="sm"
            onClick={() => setShowHistoryModal(true)}
          >
            Vital History
          </Button>
        </div>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={handleShareClick}
          className={styles.shareBtn}
        >
          {isSharing ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Loading...
            </>
          ) : (
            "Link to ABHA"
          )}
        </Button>
      </div>
      {/* No report message */}
      {!latestReport && (
        <div className="text-center text-muted py-4">
          <p>No vital report data available</p>
        </div>
      )}
      {/* Latest report card */}
      {latestReport && (
        <div className={styles.vitalCardContainer}>
          <Card key={latestReport?._id} className={styles.vitalCardNew}>
            <div className={styles.cardHeaderNew}>
              <div className={styles.vitalDateNew}>
                <strong>
                  {new Date(latestReport?.consultationDate)?.toLocaleDateString(
                    "en-GB",
                  )}{" "}
                  {new Date(latestReport?.consultationDate)?.toLocaleTimeString(
                    "en-IN",
                    {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    },
                  )}
                </strong>
              </div>
              <div className="d-flex align-items-center gap-2">
                <Button
                  variant="outline-primary"
                  size="sm"
                  className={styles.viewFullDataBtn}
                  onClick={() => handleViewAll(latestReport)}
                >
                  View Full Data
                </Button>
              </div>
            </div>
            <Card.Body className={styles.cardBodyNew}>
              <div className={styles.ecgContainer}>
                <EcgSvg />
              </div>
              <div className={styles.vitalInfoNew}>
                <div>
                  <p className="mb-1">
                    <strong>BP:</strong> {latestReport?.bloodPressure?.systolic}
                    /{latestReport?.bloodPressure?.diastolic}
                  </p>
                  <p className="mb-1">
                    <strong>Pulse:</strong> {latestReport?.pulse}
                  </p>
                </div>
                <div>
                  <p className="mb-1">
                    <strong>SpO₂:</strong> {latestReport?.spo2}%
                  </p>
                  <p className="mb-1">
                    <strong>Temp:</strong> {latestReport?.temperature} °C
                  </p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      )}
      {/* 2. All Vital Reports Table */}
      {vitalReports.length > 0 && (
        <div className={`mt-4 ${styles.invoiceWrapper}`}>
          <div className="table-responsive">
            <table className={styles.invoiceTable}>
              <thead>
                <tr>
                  <th className="text-start ps-4">Date & Time</th>

                  <th>Pulse</th>
                  <th>SpO₂</th>
                  <th>status</th>
                  <th className="text-center pe-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {vitalReports.map((report) => (
                  <tr key={report._id}>
                    <td className="text-start ps-4">
                      <div className="fw-bold text-dark">
                        {new Date(report.consultationDate).toLocaleDateString(
                          "en-GB",
                        )}
                      </div>
                      <small
                        className="text-muted"
                        style={{ fontSize: "0.75rem" }}
                      >
                        {new Date(report.consultationDate).toLocaleTimeString(
                          "en-IN",
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          },
                        )}
                      </small>
                    </td>

                    <td>
                      {report.pulse} <small className="text-muted">BPM</small>
                    </td>
                    <td>
                      <span
                        className={`${styles.statusBadge} ${
                          report.spo2 > 94 ? styles.paid : styles.pending
                        }`}
                      >
                        {report.spo2}%
                      </span>
                    </td>
                    <td>
                      <span
                        className={`${styles.statusBadge} ${
                          report.status === "Shared"
                            ? styles.Shared
                            : report.status === "Transferred"
                              ? styles.Transferred
                              : styles.Unshared
                        }`}
                      >
                        {report.status === "Shared"
                          ? "Linked"
                          : report.status === "Transferred"
                            ? "Transferred"
                            : "Not Linked"}
                      </span>
                    </td>
                    <td className="text-center pe-4">
                      <Dropdown align="end">
                        <Dropdown.Toggle className={styles.actionToggle}>
                          <BsThreeDots />
                        </Dropdown.Toggle>
                        <Dropdown.Menu className={styles.dropdownMenu}>
                          <Dropdown.Item onClick={() => handleViewAll(report)}>
                            View Full Report
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => handleEdit(report)}>
                            Edit Record
                          </Dropdown.Item>
                          <Dropdown.Item
                            className="text-danger"
                            onClick={() => confirmDelete(report._id)}
                          >
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* View Modal */}
      {viewReport && (
        <Modal
          show={showModal}
          onHide={handleClose}
          size="lg"
          centered
          className={styles.vitalModal}
        >
          <Modal.Header className={styles.modalHeader} closeButton>
            <Modal.Title>Vital Report Details</Modal.Title>
          </Modal.Header>
          <Modal.Body className={styles.modalBody}>
            <div className={styles.vitalColumns}>
              <div className={styles.column}>
                {[
                  [
                    "Consultation Date",
                    viewReport?.consultationDate
                      ? new Date(viewReport?.consultationDate)?.toLocaleString(
                          "en-GB",
                          {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: false,
                          },
                        )
                      : "N/A",
                  ],
                  ["Height", viewReport?.height ?? "N/A"],
                  ["Weight", viewReport?.weight ?? "N/A"],
                  ["BMI", viewReport?.bmi ?? "N/A"],
                  ["Pulse", viewReport?.pulse ?? "N/A"],
                ]?.map(([label, value], idx) => (
                  <div key={idx} className={styles.vitalRow}>
                    <div className={styles.vitalLabel}>{label}</div>
                    <div className={styles.vitalValue}>{value}</div>
                  </div>
                ))}
              </div>
              <div className={styles.column}>
                {[
                  ["SpO₂", viewReport?.spo2 ? `${viewReport?.spo2}%` : "N/A"],
                  [
                    "Blood Pressure",
                    viewReport?.bloodPressure
                      ? `${viewReport?.bloodPressure?.systolic} / ${viewReport?.bloodPressure?.diastolic}`
                      : "N/A",
                  ],
                  [
                    "Temperature",
                    viewReport?.temperature
                      ? `${viewReport?.temperature} °C`
                      : "N/A",
                  ],
                  ["Respiratory Rate", viewReport?.respiratoryRate ?? "N/A"],
                  [
                    "Status",
                    viewReport?.status === "Shared" ? "Linked" : "Not Linked",
                  ],
                ]?.map(([label, value], idx) => (
                  <div key={idx} className={styles.vitalRow}>
                    <div className={styles.vitalLabel}>{label}</div>
                    <div
                      className={styles.vitalValue}
                      style={{
                        color:
                          label === "Status"
                            ? value === "Not Linked"
                              ? "#ef4444"
                              : "#10b981"
                            : "#111827",
                        fontWeight: label === "Status" ? 600 : 500,
                      }}
                    >
                      {value}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className={styles.modalFooter}>
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}
      {/* Add / Update Modal */}
      <Modal
        show={showAddModal}
        onHide={handleAddClose}
        size="lg"
        centered
        className={styles.vitalFormModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editingReport ? "Update Vital Report" : "Add Vital Report"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form className={styles.vitalFormGrid}>
            <div className={styles.formGroup}>
              <label>Height</label>
              <div className="d-flex gap-2">
                <select
                  name="heightFeet"
                  value={formData?.heightFeet ?? ""}
                  onChange={handleFormChange}
                  className="form-select"
                >
                  <option>--Select--</option>
                  {[...Array(8)].map((_, i) => (
                    <option key={i + 1}>{i + 1} feet</option>
                  ))}
                </select>
                <select
                  name="heightInch"
                  value={formData?.heightInch ?? ""}
                  onChange={handleFormChange}
                  className="form-select"
                >
                  <option>--Select--</option>
                  {[...Array(12)].map((_, i) => (
                    <option key={i}>{i} inch</option>
                  ))}
                </select>
              </div>
              <small>Ex: 5 / 6 feet</small>
            </div>

            <div className={styles.formGroup}>
              <label>Weight</label>
              <input
                type="number"
                name="weight"
                value={formData?.weight ?? ""}
                onChange={handleFormChange}
                className="form-control"
                placeholder="Weight"
              />
              <small>Ex: 50 kg / 55.7 kg</small>
            </div>

            <div className={styles.formGroup}>
              <label>BMI</label>
              <input
                type="number"
                name="bmi"
                value={formData?.bmi ?? ""}
                onChange={handleFormChange}
                className="form-control"
                placeholder="BMI"
              />
            </div>

            <div className={styles.formGroup}>
              <label>Blood Pressure</label>
              <div className="d-flex gap-2">
                <input
                  type="number"
                  name="systolic"
                  value={formData?.bloodPressure?.systolic ?? ""}
                  onChange={handleFormChange}
                  className="form-control"
                  placeholder="Systolic"
                />
                <input
                  type="number"
                  name="diastolic"
                  value={formData?.bloodPressure?.diastolic ?? ""}
                  onChange={handleFormChange}
                  className="form-control"
                  placeholder="Diastolic"
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>Pulse</label>
              <input
                type="number"
                name="pulse"
                value={formData?.pulse ?? ""}
                onChange={handleFormChange}
                className="form-control"
                placeholder="Pulse"
              />
            </div>

            <div className={styles.formGroup}>
              <label>Temperature</label>
              <input
                type="number"
                name="temperature"
                value={formData?.temperature ?? ""}
                onChange={handleFormChange}
                className="form-control"
                placeholder="Temperature"
              />
            </div>

            <div className={styles.formGroup}>
              <label>SpO₂</label>
              <input
                type="number"
                name="spo2"
                value={formData?.spo2 ?? ""}
                onChange={handleFormChange}
                className="form-control"
                placeholder="SpO₂"
              />
            </div>

            <div className={styles.formGroup}>
              <label>Respiratory Rate</label>
              <input
                type="number"
                name="respiratoryRate"
                value={formData?.respiratoryRate ?? ""}
                onChange={handleFormChange}
                className="form-control"
                placeholder="Respiratory Rate"
              />
            </div>

            <div className={styles.formGroup}>
              <label className="form-label">Upload Files (PDF/Image)</label>
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.pdf"
                multiple
                onChange={handleFileChange}
                ref={fileInputRef}
                className="form-control"
              />

              {fileError && (
                <div className="text-secondary mt-1">{fileError}</div>
              )}

              {/* Existing Files */}
              {formData.patient_report?.map((url, index) => (
                <div key={index} className={styles.previewBox}>
                  <button
                    type="button"
                    className={`btn-close ${styles.closeBtn}`}
                    onClick={() => {
                      const updated = formData.patient_report.filter(
                        (_, i) => i !== index,
                      );
                      setFormData((prev) => ({
                        ...prev,
                        patient_report: updated,
                      }));
                    }}
                  ></button>

                  <div className={styles.previewWrapper}>
                    {url.endsWith(".pdf") ? (
                      <embed src={url} width="100%" height="100%" />
                    ) : (
                      <img
                        src={url}
                        className="img-fluid w-100 h-100"
                        alt="Vital preview"
                      />
                    )}
                  </div>
                </div>
              ))}

              {/* New Files Preview */}
              {files?.map((file, idx) => (
                <div key={idx} className={styles.previewBox}>
                  <button
                    type="button"
                    className={`btn-close ${styles.closeBtn}`}
                    onClick={() =>
                      setFiles((prev) => prev.filter((_, i) => i !== idx))
                    }
                  ></button>

                  <div className={styles.previewWrapper}>
                    {file.type.startsWith("image/") ? (
                      <img
                        src={URL.createObjectURL(file)}
                        className="img-fluid w-100 h-100"
                        alt="Vital preview"
                      />
                    ) : (
                      <embed
                        src={URL.createObjectURL(file)}
                        width="100%"
                        height="100%"
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.formGroup}>
              <label>Vitals Recorded On</label>
              <input
                type="datetime-local"
                name="consultationDate"
                value={formData?.consultationDate ?? ""}
                onChange={handleFormChange}
                className="form-control"
              />
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleAddClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleFormSubmit}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this report?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Share Modal */}
      <Modal
        show={showShareModal}
        onHide={() => setShowShareModal(false)}
        size="lg"
        centered
        backdrop="static"
        className={styles.shareModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>Link Vital Reports to ABHA</Modal.Title>
        </Modal.Header>

        <Modal.Body className={styles.shareModalBody}>
          {vitalReports.length > 0 ? (
            <div className="table-responsive">
              <table
                className={`table table-sm align-middle text-center ${styles.shareTable}`}
              >
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        onChange={handleSelectAll}
                        checked={
                          vitalReports.filter(
                            (r) =>
                              !["Shared", "Transferred"].includes(r.status),
                          ).length > 0 &&
                          vitalReports
                            .filter(
                              (r) =>
                                !["Shared", "Transferred"].includes(r.status),
                            )
                            .every((r) => selectedReports.includes(r._id))
                        }
                      />
                    </th>
                    <th>Date & Time</th>
                    <th>BP (S/D)</th>
                    <th>Pulse</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {vitalReports.map((item) => {
                    const isSelected = selectedReports.includes(item._id);
                    const isDisabled = ["Shared", "Transferred"].includes(
                      item.status,
                    );

                    return (
                      <tr
                        key={item._id}
                        className={
                          isDisabled
                            ? styles.sharedRow
                            : isSelected
                              ? styles.selectedRow
                              : ""
                        }
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={isSelected || isDisabled}
                            disabled={isDisabled}
                            onChange={() =>
                              handleCheckboxChange(item._id, isDisabled)
                            }
                          />
                        </td>
                        <td>
                          {new Date(item.consultationDate).toLocaleDateString(
                            "en-GB",
                          )}
                        </td>
                        <td>
                          {item.bloodPressure?.systolic}/
                          {item.bloodPressure?.diastolic}
                        </td>
                        <td>{item.pulse} BPM</td>
                        <td>
                          <span
                            className="badge"
                            style={{
                              backgroundColor:
                                item.status === "Shared"
                                  ? "#12d6ac"
                                  : "#3898cc",
                              padding: "5px 10px",
                            }}
                          >
                            {item.status === "Shared" ? "Linked" : "Not Linked"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-muted">No vital reports found.</p>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowShareModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleShareVitalReport}
            disabled={selectedReports.length === 0 || isFinalSharing}
          >
            {isFinalSharing
              ? "Linking..."
              : `Link ${selectedReports.length} Report(s)`}
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Invalid Token Modal */}
      <Modal
        show={showInvalidTokenModal}
        onHide={handleCloseInvalidModal}
        size="md"
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-semibold text-primary">Notice</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <p className="mb-0 text-muted">
            Unable to generate a link Token at the moment.
            <br />
            Please try again later.
          </p>
        </Modal.Body>
        <Modal.Footer className="border-0 d-flex justify-content-center">
          <Button variant="primary" onClick={handleCloseInvalidModal}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Linked Success & Error Modals */}
      <Modal
        show={showLinkedSuccessModal}
        onHide={() => setShowLinkedSuccessModal(false)}
        size="md"
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold text-success d-flex align-items-center">
            <BsCheckCircle className="me-2" size={24} />
            Linked Successfully!
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <p className="mb-0" style={{ fontSize: "1rem", lineHeight: "1.5" }}>
            Your health data has been successfully linked with the ABHA address.
            <br />
            You can now access and manage it seamlessly in your ABHA account.
          </p>
        </Modal.Body>
        <Modal.Footer className="border-0 d-flex justify-content-center">
          <Button
            variant="success"
            onClick={() => setShowLinkedSuccessModal(false)}
            style={{ minWidth: "100px" }}
          >
            OK
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showLinkedErrorModal}
        onHide={() => setShowLinkedErrorModal(false)}
        size="md"
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton className="border-0 pb-0"></Modal.Header>
        <Modal.Body className="text-center">
          <p className="mb-0">Something went wrong! Please try again later.</p>
        </Modal.Body>
        <Modal.Footer className="border-0 d-flex justify-content-center">
          <Button
            variant="primary"
            onClick={() => setShowLinkedErrorModal(false)}
          >
            OK
          </Button>
        </Modal.Footer>
      </Modal>
      {/* //vital history modal */}
      <Modal
        show={showHistoryModal}
        onHide={() => setShowHistoryModal(false)}
        size="xl"
        centered
        className={styles.vitalHistoryModal}
      >
        <Modal.Header closeButton className={styles.header}>
          <Modal.Title className={styles.title}>Vital History</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="table-responsive">
            <table
              className={`table table-bordered ${styles.vitalHistoryTable}`}
            >
              <thead className="table-primary">
                <tr>
                  <th>Date & Time</th>
                  <th>Height</th>
                  <th>Weight</th>
                  <th>BMI</th>
                  <th>BP (S/D)</th>
                  <th>Pulse</th>
                  <th>SpO₂</th>
                  <th>Temp</th>
                  <th>Resp Rate</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {vitalReports.map((report) => (
                  <tr key={report._id} className={styles.vitalRowHover}>
                    <td>
                      <div className="fw-bold">
                        {new Date(report.consultationDate).toLocaleDateString(
                          "en-GB",
                        )}
                      </div>
                      <small className="text-muted">
                        {new Date(report.consultationDate).toLocaleTimeString(
                          "en-IN",
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          },
                        )}
                      </small>
                    </td>
                    <td>{report.height || "N/A"}</td>
                    <td>{report.weight || "N/A"}</td>
                    <td>{report.bmi || "N/A"}</td>
                    <td>
                      <span className="text-primary fw-bold">
                        {report.bloodPressure?.systolic}/
                        {report.bloodPressure?.diastolic}
                      </span>
                    </td>
                    <td>{report.pulse || "N/A"} BPM</td>
                    <td>
                      <span
                        className={`badge ${
                          report.spo2 > 94 ? "bg-success" : "bg-danger"
                        }`}
                      >
                        {report.spo2 || 0}%
                      </span>
                    </td>
                    <td>{report.temperature || "N/A"} °C</td>
                    <td>{report.respiratoryRate || "N/A"}</td>
                    <td>
                      <span
                        className={`badge ${
                          report.status === "Shared"
                            ? "bg-success"
                            : "bg-warning text-dark"
                        }`}
                      >
                        {report.status === "Shared" ? "Linked" : "Not Linked"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal.Body>

        <Modal.Footer className="justify-content-center">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowHistoryModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default VitalReportSection;
